"use client"

import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { 
  Send, 
  TrendingUp, 
  TrendingDown, 
  MoreVertical,
  Bell,
  Share2,
  Info
} from "lucide-react"
import { SignalStatusBadge } from "./signal-status-badge"
import { cn } from "@/lib/utils"
import { Signal } from "@/hooks/use-signals"
import { sendSignalToPilot } from "@/lib/signals"

interface ImprovedSignalCardProps {
  signal: Signal
  language?: "en" | "es"
}

export function ImprovedSignalCard({
  signal,
  language = "en"
}: ImprovedSignalCardProps) {
  const isDisabled = signal.status === "closed"
  
  const handleSendToPilot = async () => {
    try {
      await sendSignalToPilot(signal.id, {
        symbol: signal.symbol,
        side: signal.side,
        entry: signal.entry,
        sl: signal.sl,
        tp: signal.tp,
        rr: signal.rr,
        status: signal.status,
        timeframe: signal.timeframe,
        confidence: signal.confidence,
        strategy: signal.strategy,
        validity: signal.validity,
      })
      
      // Show success toast or notification
      console.log('Signal sent to Pilot successfully')
    } catch (error) {
      console.error('Error sending signal to Pilot:', error)
      // Show error toast or notification
    }
  }
  
  return (
    <Card className={cn(
      "bg-background border-border/30 rounded-xl transition-all duration-300",
      "hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5",
      "group"
    )}>
      <CardContent className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr_auto] gap-6 items-center">
          
          {/* Columna 1: Asset + Chart */}
          <div className="space-y-4 min-w-[200px]">
            <div className="flex items-center gap-3">
              <Avatar className="w-10 h-10 border-2 border-primary/20">
                <AvatarImage src={`/assets/${signal.symbol.toLowerCase()}.png`} />
                <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                  {signal.symbol.slice(0, 2)}
                </AvatarFallback>
              </Avatar>
              
              <div>
                <h3 className="font-semibold text-foreground text-lg">
                  {signal.symbol}
                </h3>
                <Badge
                  variant="outline"
                  className={cn(
                    "text-xs font-medium",
                    signal.side === "LONG"
                      ? "border-primary/50 text-primary"
                      : "border-destructive/50 text-destructive"
                  )}
                >
                  {signal.side === "LONG" ? (
                    <TrendingUp className="w-3 h-3 mr-1" />
                  ) : (
                    <TrendingDown className="w-3 h-3 mr-1" />
                  )}
                  {signal.side}
                </Badge>
              </div>
            </div>
            
            {/* Mini Chart placeholder */}
            <div className="h-16 bg-muted/20 rounded-lg flex items-center justify-center">
              <span className="text-xs text-muted-foreground">Chart</span>
            </div>
            
            <p className="text-xs text-muted-foreground">
              {signal.createdAt?.toDate?.()?.toLocaleString() || 'Recently'}
            </p>
          </div>
          
          {/* Columna 2: Información Detallada */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                {language === "en" ? "Entry" : "Entrada"}
              </p>
              <p className="text-sm font-semibold text-foreground">
                ${signal.entry}
              </p>
            </div>
            
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                {language === "en" ? "Target" : "Objetivo"}
              </p>
              <p className="text-sm font-semibold text-primary">
                ${signal.tp}
              </p>
            </div>
            
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                Stop Loss
              </p>
              <p className="text-sm font-semibold text-destructive">
                ${signal.sl}
              </p>
            </div>
            
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                {language === "en" ? "Timeframe" : "Temporalidad"}
              </p>
              <Badge variant="outline" className="text-xs font-medium">
                {signal.timeframe || "1H"}
              </Badge>
            </div>
            
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                R:R
              </p>
              <p className="text-sm font-semibold text-foreground">
                {signal.rr}:1
              </p>
            </div>
          </div>
          
          {/* Columna 3: Status + Actions */}
          <div className="flex flex-col gap-3 min-w-[160px]">
            <SignalStatusBadge 
              status={signal.status} 
              language={language}
              className="justify-center"
            />
            
            <div className="flex gap-2">
              <Button
                size="sm"
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={isDisabled}
                onClick={handleSendToPilot}
              >
                <Send className="w-4 h-4 mr-2" />
                {language === "en" ? "Send to Pilot" : "Enviar a Pilot"}
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="ghost">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem>
                    <Info className="w-4 h-4 mr-2" />
                    {language === "en" ? "View Details" : "Ver Detalles"}
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Bell className="w-4 h-4 mr-2" />
                    {language === "en" ? "Set Alert" : "Crear Alerta"}
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Share2 className="w-4 h-4 mr-2" />
                    {language === "en" ? "Share" : "Compartir"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        
        {/* Sección Expandible */}
        {(signal.strategy || signal.validity) && (
          <div className="pt-4 border-t mt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  Risk/Reward
                </p>
                <p className="font-semibold text-foreground">
                  {signal.rr}:1
                </p>
              </div>
              {signal.strategy && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">
                    {language === "en" ? "Strategy" : "Estrategia"}
                  </p>
                  <p className="font-semibold text-foreground">
                    {signal.strategy}
                  </p>
                </div>
              )}
              {signal.validity && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">
                    {language === "en" ? "Validity" : "Validez"}
                  </p>
                  <p className="font-semibold text-foreground">
                    {signal.validity}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
